import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search, Plus, Minus, Trash2, ShoppingCart,
  Eye, CheckCircle, RefreshCw, CreditCard,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import Modal from '../components/Modal';
import { moneda, cantidad } from '../utils/format';

// ── Tarjeta de producto (POS) ──────────────────────────────────────────────
function ProductCard({ producto, enCarrito, onAdd }) {
  const sinStock = producto.controla_stock && producto.stock_actual <= 0;

  return (
    <button
      onClick={() => !sinStock && onAdd(producto)}
      disabled={sinStock}
      className={`
        relative w-full text-left p-4 rounded-2xl border-2 transition-all duration-150 select-none
        ${sinStock
          ? 'border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed'
          : enCarrito
            ? 'border-primary-400 bg-primary-50 shadow-md'
            : 'border-slate-200 bg-white hover:border-primary-300 hover:shadow-sm active:scale-[0.98]'}
      `}
    >
      {enCarrito > 0 && (
        <span className="absolute top-2 right-2 w-6 h-6 bg-primary-500 text-white rounded-full text-xs flex items-center justify-center font-bold">
          {enCarrito}
        </span>
      )}
      <p className="font-semibold text-slate-900 text-sm leading-tight pr-6">{producto.nombre}</p>
      <p className="text-primary-600 font-bold mt-1">{moneda(producto.precio_venta)}</p>
      {producto.controla_stock ? (
        <p className={`text-xs mt-1 ${sinStock ? 'text-red-500' : producto.stock_actual <= producto.stock_minimo && producto.stock_minimo > 0 ? 'text-orange-500' : 'text-slate-400'}`}>
          {sinStock ? 'Sin stock' : `Stock: ${cantidad(producto.stock_actual, producto.unidad)}`}
        </p>
      ) : (
        <p className="text-xs mt-1 text-slate-300">Servicio</p>
      )}
    </button>
  );
}

// ── Fila del carrito ───────────────────────────────────────────────────────
function CartRow({ item, onQty, onRemove }) {
  return (
    <div className="flex items-center gap-3 py-3 border-b border-slate-100 last:border-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900 truncate">{item.nombre}</p>
        <p className="text-xs text-slate-400">{moneda(item.precio_unitario)} c/u</p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button
          onClick={() => onQty(item.producto_id, item.cantidad - 1)}
          className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
        >
          <Minus size={13} />
        </button>
        <span className="w-8 text-center text-sm font-semibold tabular-nums">
          {item.cantidad % 1 === 0 ? item.cantidad : item.cantidad.toFixed(3)}
        </span>
        <button
          onClick={() => onQty(item.producto_id, item.cantidad + 1)}
          className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
        >
          <Plus size={13} />
        </button>
      </div>
      <div className="w-20 text-right shrink-0">
        <p className="text-sm font-semibold tabular-nums">{moneda(item.precio_unitario * item.cantidad)}</p>
      </div>
      <button
        onClick={() => onRemove(item.producto_id)}
        className="p-1 rounded-lg hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors shrink-0"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

// ── Modal detalle de venta ─────────────────────────────────────────────────
function DetalleVentaModal({ venta, onClose }) {
  if (!venta) return null;
  const fechaStr = new Date(venta.fecha).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
  return (
    <Modal
      isOpen={!!venta}
      onClose={onClose}
      title={`Venta #${venta.numero ?? venta.id}`}
      size="md"
      footer={<button className="btn-secondary" onClick={onClose}>Cerrar</button>}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <div><span className="text-slate-400">Fecha:</span> <span className="font-medium">{fechaStr}</span></div>
          <div><span className="text-slate-400">Operador:</span> <span className="font-medium">{venta.usuario}</span></div>
          {venta.cliente && (
            <div><span className="text-slate-400">Cliente:</span> <span className="font-medium">{venta.cliente}</span></div>
          )}
          {venta.forma_pago && (
            <div><span className="text-slate-400">Forma de pago:</span> <span className="font-medium">{venta.forma_pago}</span></div>
          )}
        </div>

        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr><th>Producto</th><th>Cant.</th><th>Precio</th><th>Subtotal</th></tr>
            </thead>
            <tbody>
              {venta.items?.map((item, i) => (
                <tr key={i}>
                  <td className="font-medium">{item.producto}</td>
                  <td className="tabular-nums">{cantidad(item.cantidad, item.unidad)}</td>
                  <td className="tabular-nums">{moneda(item.precio_unitario)}</td>
                  <td className="tabular-nums font-medium">{moneda(item.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="space-y-1 text-sm">
          <div className="flex justify-between text-slate-500">
            <span>Subtotal</span><span className="tabular-nums">{moneda(venta.subtotal)}</span>
          </div>
          {venta.descuento > 0 && (
            <div className="flex justify-between text-emerald-600">
              <span>Descuento</span><span className="tabular-nums">-{moneda(venta.descuento)}</span>
            </div>
          )}
          <div className="flex justify-between font-bold text-slate-900 text-base pt-2 border-t border-slate-100 mt-2">
            <span>Total</span><span className="tabular-nums">{moneda(venta.total)}</span>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ── Page principal ─────────────────────────────────────────────────────────
export default function Ventas() {
  const [tab, setTab] = useState('pos');

  // POS
  const [productos,     setProductos]     = useState([]);
  const [formasPago,    setFormasPago]    = useState([]);
  const [loadingProds,  setLoadingProds]  = useState(true);
  const [search,        setSearch]        = useState('');
  const [cart,          setCart]          = useState([]);
  const [descuento,     setDescuento]     = useState('0');
  const [formaPagoId,   setFormaPagoId]   = useState('');
  const [confirming,    setConfirming]    = useState(false);
  const [cartOpen,      setCartOpen]      = useState(false);

  // Historial
  const [ventas,          setVentas]          = useState([]);
  const [loadingVentas,   setLoadingVentas]   = useState(false);
  const [detalle,         setDetalle]         = useState(null);
  const [loadingDetalle,  setLoadingDetalle]  = useState(false);

  // ── Carga inicial ─────────────────────────────────────────────────────────
  const fetchProductos = useCallback(async () => {
    setLoadingProds(true);
    try {
      const res = await api.get('/productos/index.php');
      setProductos(res.data);
    } catch { toast.error('Error al cargar productos'); }
    finally { setLoadingProds(false); }
  }, []);

  useEffect(() => {
    fetchProductos();
    api.get('/formas-pago/index.php')
      .then(r => {
        setFormasPago(r.data);
        // Pre-seleccionar "Efectivo" si existe
        const efectivo = r.data.find(f => f.tipo === 'efectivo');
        if (efectivo) setFormaPagoId(String(efectivo.id));
      })
      .catch(() => {});
  }, [fetchProductos]);

  const prodsFiltrados = useMemo(() =>
    productos.filter(p =>
      p.nombre.toLowerCase().includes(search.toLowerCase()) ||
      (p.sku && p.sku.toLowerCase().includes(search.toLowerCase()))
    ),
    [productos, search]
  );

  // ── Carrito ───────────────────────────────────────────────────────────────
  const addToCart = (producto) => {
    setCart(prev => {
      const existe = prev.find(i => i.producto_id === producto.id);
      if (existe) {
        if (producto.controla_stock && existe.cantidad >= producto.stock_actual) {
          toast.error('No hay más stock disponible');
          return prev;
        }
        return prev.map(i =>
          i.producto_id === producto.id ? { ...i, cantidad: i.cantidad + 1 } : i
        );
      }
      return [...prev, {
        producto_id:     producto.id,
        nombre:          producto.nombre,
        precio_unitario: producto.precio_venta,
        stock_actual:    producto.stock_actual,
        controla_stock:  producto.controla_stock,
        unidad:          producto.unidad,
        cantidad:        1,
      }];
    });
  };

  const setQty = (productoId, qty) => {
    if (qty <= 0) { removeFromCart(productoId); return; }
    const prod = productos.find(p => p.id === productoId);
    if (prod?.controla_stock && qty > prod.stock_actual) {
      toast.error('Cantidad supera el stock disponible');
      return;
    }
    setCart(prev => prev.map(i => i.producto_id === productoId ? { ...i, cantidad: qty } : i));
  };

  const removeFromCart = (productoId) => setCart(prev => prev.filter(i => i.producto_id !== productoId));
  const clearCart = () => { setCart([]); setDescuento('0'); };

  const subtotal       = cart.reduce((s, i) => s + i.precio_unitario * i.cantidad, 0);
  const descuentoNum   = Math.min(parseFloat(descuento) || 0, subtotal);
  const total          = subtotal - descuentoNum;
  const cartQty        = cart.reduce((s, i) => s + i.cantidad, 0);

  // ── Confirmar venta ───────────────────────────────────────────────────────
  const handleConfirmar = async () => {
    if (cart.length === 0) { toast.error('El carrito está vacío'); return; }
    setConfirming(true);
    try {
      await api.post('/ventas/index.php', {
        forma_pago_id: formaPagoId ? parseInt(formaPagoId) : null,
        descuento:     descuentoNum,
        items: cart.map(i => ({
          producto_id:     i.producto_id,
          cantidad:        i.cantidad,
          precio_unitario: i.precio_unitario,
        })),
      });
      toast.success(`Venta registrada · ${moneda(total)}`);
      clearCart();
      setCartOpen(false);
      fetchProductos();
    } catch (err) {
      toast.error(err?.message || 'Error al registrar la venta');
    } finally {
      setConfirming(false);
    }
  };

  // ── Historial ─────────────────────────────────────────────────────────────
  const fetchVentas = useCallback(async () => {
    setLoadingVentas(true);
    try {
      const res = await api.get('/ventas/index.php');
      setVentas(res.data);
    } catch { toast.error('Error al cargar historial'); }
    finally { setLoadingVentas(false); }
  }, []);

  useEffect(() => { if (tab === 'historial') fetchVentas(); }, [tab, fetchVentas]);

  const verDetalle = async (id) => {
    setLoadingDetalle(true);
    try {
      const res = await api.get(`/ventas/index.php?id=${id}`);
      setDetalle(res.data);
    } catch { toast.error('Error al cargar detalle'); }
    finally { setLoadingDetalle(false); }
  };

  // ── Panel del carrito (desktop + mobile drawer) ───────────────────────────
  const CartPanel = () => (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <ShoppingCart size={17} />
          Carrito
          {cart.length > 0 && <span className="badge-neutral">{cart.length}</span>}
        </h3>
        {cart.length > 0 && (
          <button onClick={clearCart} className="text-xs text-slate-400 hover:text-red-500 transition-colors">
            Vaciar
          </button>
        )}
      </div>

      {cart.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-slate-200 gap-2 select-none">
          <ShoppingCart size={40} strokeWidth={1.5} />
          <p className="text-sm text-slate-400">Seleccioná productos</p>
        </div>
      ) : (
        <>
          {/* Ítems */}
          <div className="flex-1 overflow-y-auto -mx-1 px-1">
            {cart.map(item => (
              <CartRow key={item.producto_id} item={item} onQty={setQty} onRemove={removeFromCart} />
            ))}
          </div>

          {/* Forma de pago */}
          <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
            {formasPago.length > 0 && (
              <div className="flex items-center gap-2">
                <CreditCard size={14} className="text-slate-400 shrink-0" />
                <select
                  className="input text-sm flex-1 py-1.5"
                  value={formaPagoId}
                  onChange={e => setFormaPagoId(e.target.value)}
                >
                  <option value="">Sin especificar</option>
                  {formasPago.map(f => (
                    <option key={f.id} value={f.id}>{f.nombre}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Descuento */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-500 shrink-0 w-20">Descuento $</label>
              <input
                className="input text-right text-sm flex-1 py-1.5"
                type="number" min="0" step="0.01"
                value={descuento}
                onChange={e => setDescuento(e.target.value)}
              />
            </div>

            {/* Totales */}
            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-slate-500">
                <span>Subtotal</span>
                <span className="tabular-nums">{moneda(subtotal)}</span>
              </div>
              {descuentoNum > 0 && (
                <div className="flex justify-between text-emerald-600">
                  <span>Descuento</span>
                  <span className="tabular-nums">-{moneda(descuentoNum)}</span>
                </div>
              )}
              <div className="flex justify-between items-baseline font-bold text-slate-900 pt-1">
                <span className="text-base">TOTAL</span>
                <span className="text-2xl tabular-nums text-primary-600">{moneda(total)}</span>
              </div>
            </div>

            <button
              onClick={handleConfirmar}
              disabled={confirming || cart.length === 0}
              className="btn-primary btn-lg w-full text-base mt-1"
            >
              {confirming ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Procesando...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <CheckCircle size={19} /> Confirmar venta
                </span>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Header con tabs */}
      <div className="flex items-center justify-between">
        <h1 className="page-title">Ventas</h1>
        <div className="flex bg-slate-100 rounded-xl p-1 gap-1">
          {[{ key: 'pos', label: 'Nueva venta' }, { key: 'historial', label: 'Historial' }].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all
                ${tab === t.key ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── POS ─────────────────────────────────────────────────────────────── */}
      {tab === 'pos' && (
        <div className="flex gap-5 h-[calc(100vh-180px)]">
          {/* Grilla de productos */}
          <div className="flex-1 flex flex-col min-w-0">
            <div className="relative mb-4">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                className="input pl-9"
                placeholder="Buscar por nombre o SKU..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>

            {loadingProds ? (
              <div className="flex-1 flex items-center justify-center text-slate-300">
                <RefreshCw size={28} className="animate-spin" />
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto">
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 pb-4">
                  {prodsFiltrados.map(p => (
                    <ProductCard
                      key={p.id}
                      producto={p}
                      enCarrito={cart.find(i => i.producto_id === p.id)?.cantidad ?? 0}
                      onAdd={addToCart}
                    />
                  ))}
                  {prodsFiltrados.length === 0 && (
                    <div className="col-span-full text-center py-12 text-slate-300 text-sm select-none">
                      No hay productos{search ? ' con ese criterio' : ''}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Carrito — desktop */}
          <div className="hidden lg:flex w-80 xl:w-96 bg-white border border-slate-200 rounded-2xl p-5 flex-col shrink-0">
            <CartPanel />
          </div>
        </div>
      )}

      {/* Botón flotante — mobile */}
      {tab === 'pos' && cart.length > 0 && (
        <button
          onClick={() => setCartOpen(true)}
          className="fixed bottom-6 right-6 lg:hidden btn-primary shadow-lg rounded-full pl-5 pr-6 py-3 text-base z-30"
        >
          <ShoppingCart size={20} />
          {cartQty} ítem/s · {moneda(total)}
        </button>
      )}

      {/* Drawer carrito — mobile */}
      {cartOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCartOpen(false)} />
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl p-5 max-h-[90vh] flex flex-col">
            <CartPanel />
          </div>
        </div>
      )}

      {/* ── Historial ─────────────────────────────────────────────────────── */}
      {tab === 'historial' && (
        <div className="card !p-0 overflow-hidden">
          {loadingVentas ? (
            <div className="flex justify-center py-16">
              <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
            </div>
          ) : ventas.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-300">
              <ShoppingCart size={48} strokeWidth={1.5} />
              <p className="text-sm mt-3 text-slate-400">No hay ventas registradas</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Fecha / Hora</th>
                    <th>Cliente</th>
                    <th>Forma de pago</th>
                    <th>Ítems</th>
                    <th>Descuento</th>
                    <th>Total</th>
                    <th>Operador</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {ventas.map(v => {
                    const fecha = new Date(v.fecha).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
                    return (
                      <tr key={v.id}>
                        <td className="tabular-nums text-slate-400 text-xs font-mono">#{v.numero}</td>
                        <td className="tabular-nums text-slate-600 text-sm">{fecha}</td>
                        <td className="text-slate-500 text-sm">{v.cliente || <span className="text-slate-300">Mostrador</span>}</td>
                        <td className="text-slate-500 text-sm">{v.forma_pago || <span className="text-slate-300">—</span>}</td>
                        <td className="text-slate-500">{v.num_items}</td>
                        <td className="tabular-nums text-emerald-600">
                          {v.descuento > 0 ? `-${moneda(v.descuento)}` : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="tabular-nums font-bold text-slate-900">{moneda(v.total)}</td>
                        <td className="text-slate-400 text-xs">{v.usuario}</td>
                        <td>
                          <button
                            onClick={() => verDetalle(v.id)}
                            disabled={loadingDetalle}
                            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
                          >
                            <Eye size={15} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <DetalleVentaModal venta={detalle} onClose={() => setDetalle(null)} />
    </div>
  );
}
